/**
 * Self-service API client — `/api/admin/me/*`.
 *
 * Backed by `backend/src/hub/routes/me.py`. These endpoints are
 * available to any authenticated user (no admin role required) and
 * always operate on the current token's owner.
 */
import { api } from "./api";

export interface MyProfile {
  user_id: string;
  username: string;
  email: string;
  full_name: string | null;
  first_name: string | null;
  last_name: string | null;
  organization: string | null;
  state: string;
  groups: string[];
  /** Flattened effective permission strings ("resource:action", may
   *  include wildcards). Drives permission-aware navigation — see
   *  `lib/permissions.ts`. The backend route gates stay authoritative. */
  permissions: string[];
  preferences: {
    language?: "en" | "it";
    theme?: "light" | "dark" | "system";
    sidebar_collapsed?: boolean;
    [k: string]: unknown;
  };
  /** Set ⇒ a central Account Tray member (ADR-0077): the Topbar offers the
   *  workspace switcher. null = a local account. */
  identity_id?: string | null;
  last_login_at: string | null;
  created_at: string;
  /** The running image's version (= deployed release tag, or "dev" locally).
   *  Shown in the sidebar so the label always matches what's deployed. */
  app_version: string;
}

export interface ProfileUpdate {
  email?: string;
  first_name?: string | null;
  last_name?: string | null;
  organization?: string | null;
}

export interface PreferencesUpdate {
  language?: "en" | "it";
  theme?: "light" | "dark" | "system";
  sidebar_collapsed?: boolean;
}

export interface MyApiKeySummary {
  key_id: string;
  name: string | null;
  /** F5: the key's OWN permission list (used when the key authenticates
   *  a request — not the owner's group permissions). */
  permissions: string[];
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

export interface MyApiKeyCreated extends MyApiKeySummary {
  value: string;
}

export const meApi = {
  get: () => api.get<MyProfile>("/me"),
  update: (body: ProfileUpdate) => api.patch<MyProfile>("/me", body),
  updatePreferences: (body: PreferencesUpdate) => api.patch<MyProfile>("/me/preferences", body),

  listApiKeys: () => api.get<MyApiKeySummary[]>("/me/api-keys"),
  // `permissions` must be a SUBSET of the caller's own grants (403
  // otherwise) — a self-service key can only narrow, never escalate.
  createApiKey: (name?: string, expires_at?: string, permissions?: string[]) =>
    api.post<MyApiKeyCreated>("/me/api-keys", { name, expires_at, permissions }),
  revokeApiKey: (keyId: string) => api.delete<void>(`/me/api-keys/${keyId}`),
};
